import type { Position, PositionLeg } from '@/types/generated/common/trading'

/**
 * A venue that holds several sub-positions per instrument (Papertrade) sends them as
 * `Position.legs`, each with its own liquidation price; every other venue sends none.
 * One leg is the position itself, so a breakdown only exists from two up.
 */
export function positionLegs(position: Position): PositionLeg[] {
  const legs = position.legs ?? []
  return legs.length > 1 ? legs : []
}

/** Legs whose liquidation prices are this close (fraction of price) share one chart line. */
const LIQ_LINE_MERGE_PCT = 0.001

export interface LiquidationLevel {
  price: number
  /** Legs liquidated at this level. */
  legCount: number
  /** Their summed size, in the position's size denomination. */
  quantity: number
}

/** Chart label tail for a level: ` ×N` when legs merged, then their summed size. */
export function liquidationLevelSuffix(
  level: LiquidationLevel,
  formatQuantity: (quantity: number) => string
): string {
  return `${level.legCount > 1 ? ` ×${level.legCount}` : ''} · ${formatQuantity(level.quantity)}`
}

/**
 * The chart's liquidation lines for `legs`, nearest `reference` first. Legs within
 * LIQ_LINE_MERGE_PCT of each other merge only for the same position side and
 * the same side of the reference, drawn at the price of
 * whichever of them `reference` reaches first.
 */
export function liquidationLevels(legs: PositionLeg[], reference: number): LiquidationLevel[] {
  if (legs.length === 0) return []
  const priced = legs
    .map((leg) => ({
      side: leg.side,
      price: Number(leg.liquidationPrice),
      quantity: Number(leg.quantity),
    }))
    .filter((leg) => Number.isFinite(leg.price) && leg.price > 0)
    .sort((a, b) => a.price - b.price)

  const levels: LiquidationLevel[] = []
  const groups = new Map<string, { anchor: number; level: LiquidationLevel }>()
  for (const leg of priced) {
    const groupKey = `${leg.side}:${leg.price < reference ? 'below' : 'above'}`
    const group = groups.get(groupKey)
    const last = group?.level
    if (group && last && leg.price - group.anchor <= group.anchor * LIQ_LINE_MERGE_PCT) {
      last.legCount += 1
      last.quantity += Number.isFinite(leg.quantity) ? leg.quantity : 0
      if (Math.abs(leg.price - reference) < Math.abs(last.price - reference)) last.price = leg.price
      continue
    }
    const level = {
      price: leg.price,
      legCount: 1,
      quantity: Number.isFinite(leg.quantity) ? leg.quantity : 0,
    }
    groups.set(groupKey, { anchor: leg.price, level })
    levels.push(level)
  }
  return levels.sort((a, b) => Math.abs(a.price - reference) - Math.abs(b.price - reference))
}
